import Modal, {ModalFooter} from '../shared/Modal.js';
import Button from '../shared/Button.js';
import Collapsible from '../shared/Collapsible.js';
import './pigBatchesGrid.css';
// ============================================================================
// src/pig/PigBatchesView.tsx  —  Phase 2 Round 6
// ----------------------------------------------------------------------------
// Pig feeder batches admin view (largest inline-JSX view by line count —
// ~662 lines). It is TWO surfaces: pig.batches.index is this component with
// `recordMode` false, and pig.batches.detail is the same component with it
// true, rendering <PigBatchPage> INLINE rather than through a router split.
//
// B3.3: the six reads both surfaces render from — ppp-feeders-v1,
// ppp-breeding-v1, ppp-farrowing-v1, ppp-breeders-v1, ppp-boars-v1 and the
// pig_dailys history — are owned by `pig.batches.core` and read once at the top
// of this component, above the recordMode branch. They used to arrive through
// usePig() and useDailysRecent() off App's boot transaction. The feeder and
// trip forms still live in PigContext because they are UI state, but the three
// debounce refs and ppp-feeders-v1 writer live here now too. That makes this
// component renderable without an App-owned prop on a released route.
// ============================================================================
import React from 'react';
import {sb} from '../lib/supabase.js';
import {fmtS} from '../lib/dateUtils.js';
import {S} from '../lib/styles.js';

import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import {
  calcBreedingTimeline,
  calcCycleStatus,
  buildCycleSeqMap,
  cycleLabel,
  pigTransfersForSub,
  pigTripPigsForSub,
  pigTripPigsAttributed,
  pigMortalityForSub,
  calcAgeRange as libCalcAgeRange,
  pigSlug,
  DEFAULT_BOAR_NAMES,
} from '../lib/pig.js';
// The route-local owner of every read both pig batch surfaces render from.
// pig.batches.core replaces the five usePig() blobs and the useDailysRecent()
// history that used to arrive off App's boot transaction.
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {PigBatchesCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {buildPigBatchGridMetrics} from '../lib/pigBatchGridMetrics.js';
import {
  loadPigForecastEntries,
  loadPigForecastSessions,
  loadPigGlobalAdg,
  persistPigGlobalAdg,
} from '../lib/pigBatchesHubApi.js';
import {createConfirmedDraftAutosave} from '../shared/confirmedDraftAutosave.js';
import {pigAddPlannedTripsBatch, pigSyncProcessingTripNumbers} from '../lib/pigPlannerApi.js';
import type {PigPlannedTripAllocation} from '../lib/pigPlannerApi.js';
import type {PigGlobalAdgRow as GlobalAdgRow} from '../lib/pigBatchesHubApi.js';
import {
  PLANNED_TRIP_TARGET_WEIGHT_LBS,
  allocatePlannedTrips,
  buildFarrowingAgeDistribution,
  projectFarrowingAgeWindow,
  seedGlobalADG,
} from '../lib/pigForecast.js';
// The `no-unused-vars` disables the six JSX-only imports in this file used to
// carry are deleted, not retargeted: the core rule is off for TypeScript files
// and @typescript-eslint/no-unused-vars resolves JSX references, so the
// directives themselves reported as unused.
import InlineNotice from '../shared/InlineNotice.jsx';
import Badge from '../shared/Badge.jsx';
import PlannerIcon from '../components/PlannerIcon.jsx';
import {
  useAppDraftGuard,
  useAppLocation as useLocation,
  useAppNavigate as useNavigate,
} from '../app/navigation/index.js';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';
import {buildPigBatchComparator} from '../lib/pigBatchFilters.js';

import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import type {PigBreederRow, PigBreedingCycle, PigFarrowingRecord, PigFeederGroup} from '../lib/pig.js';
import type {Notice} from '../shared/InlineNotice.js';
import type {RecordSeqEntry} from '../lib/recordSequence.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {usePig} from '../contexts/PigContext.jsx';
import PigBatchHubTile, {PIG_BATCH_GRID_COLUMNS, PIG_BATCH_GRID_HEADERS} from './PigBatchHubTile.jsx';
import {usePigMortality} from './usePigMortality.js';
import {createFeederOpRunner} from './pigFeederOps.js';
import PigConflictNotice, {type PigConflictState} from './PigConflictNotice.js';
import {
  expectedFrom,
  newPigRequestId,
  pigRejectionMessage,
  type PigConflict,
  type PigOpOutcome,
} from '../lib/pigOperationsApi.js';
import {usePigSubBatches} from './usePigSubBatches.js';
import {usePigPlannedTrips} from './usePigPlannedTrips.js';
import {usePigProcessingTrips} from './usePigProcessingTrips.js';
import type {PigTripForm} from './usePigProcessingTrips.js';
import PigBatchPage from './PigBatchPage.tsx';
import {useUI} from '../contexts/UIContext.jsx';
import {RecordPageLoading, RecordPageNotFound} from '../shared/RecordPageShell.jsx';
import Header from '../shared/Header.tsx';

// Human labels for the parent-batch fields diffed on close for the one-summary
// pig.batch field.updated Activity emit (stable-baseline diffing).
const PIG_BATCH_FIELD_LABELS = {
  batchName: 'Batch name',
  cycleId: 'Linked cycle',
  giltCount: 'Gilt count',
  boarCount: 'Boar count',
  originalPigCount: 'Started head',
  perLbFeedCost: 'Feed cost / lb',
  legacyFeedLbs: 'Legacy feed (lbs)',
  feedAllocatedToTransfers: 'Feed allocated to transfers',
  startDate: 'Start date',
  notes: 'Notes',
  status: 'Status',
};
// What the batch form may change through feeder.update (231's allowlist): the
// fields the form edits. cycleId is fixed once linked, perLbFeedCost is set in
// Admin › Feed Costs, legacyFeedLbs has no input, originalPigCount is derived
// by the server, and status goes through archive / reopen.
const FEEDER_EDIT_FIELDS = ['batchName', 'giltCount', 'boarCount', 'startDate', 'notes', 'feedAllocatedToTransfers'];
const FEEDER_COUNT_FIELDS = ['giltCount', 'boarCount'];

// The form's values as the server accepts them: whole counts, a non-negative
// transfer feed figure, text otherwise (231's _pig_check_fields).
function feederChangeFrom(form: Record<string, unknown>): Record<string, unknown> {
  const whole = (v: unknown) => Math.max(0, parseInt(String(v ?? '')) || 0);
  return {
    batchName: String(form.batchName ?? ''),
    giltCount: whole(form.giltCount),
    boarCount: whole(form.boarCount),
    startDate: String(form.startDate ?? ''),
    notes: String(form.notes ?? ''),
    feedAllocatedToTransfers: Math.max(0, parseFloat(String(form.feedAllocatedToTransfers ?? '')) || 0),
  };
}

// Whether the stored value already MEANS the form's value. A legacy string
// count "5" is the count 5, and a missing number is 0, so neither is an edit.
function sameFeederValue(field: string, stored: unknown, next: unknown): boolean {
  if (FEEDER_COUNT_FIELDS.includes(field) || field === 'feedAllocatedToTransfers') {
    const n = stored === '' || stored == null ? 0 : Number(stored);
    return n === next;
  }
  return (stored ?? '') === next;
}

// A stored value as the form holds it (for "Use latest").
function feederFormValue(field: string, stored: unknown): unknown {
  if (FEEDER_COUNT_FIELDS.includes(field) || field === 'feedAllocatedToTransfers') {
    return stored === '' || stored == null ? 0 : Number(stored) || 0;
  }
  return stored == null ? '' : String(stored);
}

// The four pig hooks declare their timer dependency as
// `MutableRefObject<ReturnType<typeof setTimeout> | undefined>`, while these
// route-local refs start at `null`. The hooks only clear and replace the value,
// so the cast at each handoff preserves the legacy debounce behavior without
// widening the hook contracts beyond this one mount.
type HookTimerRef = React.MutableRefObject<ReturnType<typeof setTimeout> | undefined>;

// The inline notice shape the pig hooks declare (src/pig/usePigMortality.ts's
// PigNotice). Deliberately wider than src/shared/InlineNotice.tsx's `Notice`,
// whose `kind` is a closed union — the seam is pre-existing and the render sites
// below assert across it.
type PigBatchNotice = {kind: string; message: string};

// A feeder batch row as this hub reads it. src/lib/pig.ts's PigFeederGroup
// declares the columns its own helpers use; `notes` and `perLbFeedCost` are the
// two the form and the export additionally touch, and PigFeederGroup leaves
// both to its index signature.
// `id` is required here while PigFeederGroup makes it optional: the hub keys
// its metrics map, its route and its record lookup by it, all unguarded, and
// the raw-source guards pin those expressions byte-for-byte.
type PigBatchRow = PigFeederGroup & {
  id: string;
  // Required for the same reason as `id`, and matching PigBatchPage's own
  // declaration of the row it is handed: the tile, the export and the record
  // page all read the name unguarded.
  batchName: string;
  notes?: string | null;
  perLbFeedCost?: number | string | null;
};

// The feeder-batch form. See the narrowing comment at the usePig() read.
type PigBatchFeederForm = {
  batchName: string;
  cycleId: string;
  giltCount: number | string;
  boarCount: number | string;
  startDate: string;
  originalPigCount: number | string;
  perLbFeedCost: number | string;
  legacyFeedLbs: number | string;
  // Not on PigContext's seed literal and not written by the hub's Add-Batch
  // opener either — only the record page's edit opener and the save paths set
  // it, which is exactly what optional records.
  feedAllocatedToTransfers?: number | string;
  notes: string;
  status: string;
};

// An app_saved_views row, reduced to the columns this hub reads. Same shape as
// the sibling declaration in src/pig/PigDailysView.tsx, over the same table.

// The saved filter payload, exactly as pigBatchesViewState() writes it.

// The originating list order handed through route state; absent on a direct
// link, which is what hides the prev/next controls.
type PigBatchesLocation = {pathname: string; state?: {recordSeq?: RecordSeqEntry[] | null} | null};

// The ppp-pig-global-adg-v1 app_store row is declared by the module that reads
// and writes it — src/lib/pigBatchesHubApi.ts — and imported here under the name
// this file has always used it by.

// The two weigh-in projections the ADG seed reads, as their `select(...)` lists
// name them.
type PigForecastSession = {
  id: string;
  batch_id?: string | null;
  date: string;
  status?: string | null;
  started_at?: string | null;
};
type PigForecastEntry = {session_id?: string | null; weight?: number | string | null};

// A pig_dailys row as the feed matcher reads it.
type PigBatchesDailyRow = {batch_label?: string | null; batch_id?: string | null; feed_lbs?: number | string | null};

// The hub's filter + sort state. Both shapes are src/lib/pigBatchFilters.ts's
// own declarations — the same module whose predicate and comparator read them —
// so the persisted state and the pure filter lib cannot drift apart.

// The per-batch grid metrics buildPigBatchGridMetrics returns, plus the
// `feedPerStarted` alias this view adds so the sort keys and the rendered cells
// read the same number.
type PigBatchGridMetrics = ReturnType<typeof buildPigBatchGridMetrics> & {feedPerStarted: number | null};

// The not-in-record-mode value of recordRawId. `null` at runtime, exactly as
// before; typed as the string arm because recordRawId is only READ inside
// `if (recordMode)`, where the ternary took that arm — and the decodeURIComponent
// call there is pinned byte-for-byte by a raw-source guard, so the assertion
// lives on the declaration rather than on the call.
const NO_RECORD_ID = null as unknown as string;

// The empty params object, hoisted to module scope so it is one stable
// reference. useRouteResource compares params by their serialization, so an
// inline `{}` would not rebuild the key — but every other localized pig view
// declares it this way and a fresh object per render is still churn.
const PIG_BATCHES_PARAMS = Object.freeze({});

// The pre-settle value of every array pig.batches.core owns. One shared frozen
// reference rather than a fresh `[]` per read: feederGroups, breedingCycles and
// farrowingRecs are dependencies of the systemAdgEstimate memo AND of the
// auto-allocate effect that persists planned trips, so a new identity on every
// render of the loading window would re-run both on every render.
const EMPTY_PIG_BATCHES_ROWS: readonly never[] = Object.freeze([]);

export default function PigBatchesView() {
  // Read once here; the two forwarded hook chains (usePigSubBatches,
  // usePigProcessingTrips) and the pigBatchPageView bundle all close over
  // this binding, so they get the live opener without any prop plumbing.
  const {confirmDelete} = useConfirm();
  // Inline notice shared across this view's modals + page-level actions
  // (mortality, sub-batch, feeder form, ADG input). Cleared on each
  // action entry; failure paths set + return.
  const [notice, setNotice] = React.useState<PigBatchNotice | null>(null);
  const [showArchBatches, setShowArchBatches] = usePersistentViewState('pig.batches.completeOpen', false);
  // Tracks whether the parent modal's partition editor has unsaved sub-batch
  // count changes. closeFeederForm reads this so a fast close (<1.5s) still
  // flushes the pending partition write — the shared pigAutoSaveTimer would
  // otherwise be cleared by closeFeederForm and the change lost on reload.
  const partitionDirtyRef = React.useRef(false);
  const pigAutoSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const subAutoSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const tripAutoSaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const {authState, setSaveStatus} = useAuth();
  // ONLY THE FORM STATE COMES FROM usePig NOW. The five
  // former-eager values that used to be destructured here — breedingCycles,
  // breeders, farrowingRecs, feederGroups and useDailysRecent().pigDailys — are
  // read through pig.batches.core below, and so is the sixth (boarNames) that
  // PigBatchPage used to take off the provider itself. `feedersLoaded` went
  // with them: it was App's boot readiness flag for exactly this record gate,
  // and once the array is read locally it cannot answer for this page.
  const {
    feederForm: feederFormRaw,
    setFeederForm: setFeederFormRaw,
    originalFeederForm: originalFeederFormRaw,
    setOriginalFeederForm,
    showFeederForm,
    setShowFeederForm,
    editFeederId: editFeederIdRaw,
    setEditFeederId,
    activeTripBatchId: activeTripBatchIdRaw,
    setActiveTripBatchId,
    tripForm,
    setTripForm,
    editTripId: editTripIdRaw,
    setEditTripId,
  } = usePig();
  const batchesResource = useRouteResource(RESOURCE.PIG_BATCHES_CORE, PIG_BATCHES_PARAMS);
  const batchesData = batchesResource.data as PigBatchesCoreData | null;
  // Writers publish this route-local override only after confirmation. The
  // partition editor also keeps an explicit unsaved draft here; its dirty flag
  // survives failure and close/navigation flushes it. The local revision guard
  // prevents an earlier confirmation from replacing a newer partition draft.
  // A remount re-reads the server; whole-document conflict safety is separate.
  const [savedFeederGroups, setSavedFeederGroups] = React.useState<PigBatchRow[] | null>(null);
  // The one place this view states what it believes the opaque arrays hold. The
  // shapes are src/lib/pig.ts's; the batch rows additionally carry the two
  // columns named on PigBatchRow. The pre-settle fallback is the shared frozen
  // constant, NOT a fresh `[]`: three of these four are dependencies of a memo
  // and of the auto-allocate effect that WRITES, so minting a new array on
  // every render of the loading window would re-run both on every render.
  const breedingCycles = (batchesData?.breedingCycles ?? EMPTY_PIG_BATCHES_ROWS) as unknown as PigBreedingCycle[];
  const breeders = (batchesData?.breeders ?? EMPTY_PIG_BATCHES_ROWS) as unknown as PigBreederRow[];
  const farrowingRecs = (batchesData?.farrowingRecs ?? EMPTY_PIG_BATCHES_ROWS) as unknown as PigFarrowingRecord[];
  const feederGroups =
    savedFeederGroups ?? ((batchesData?.feederGroups ?? EMPTY_PIG_BATCHES_ROWS) as unknown as PigBatchRow[]);
  // Never undefined: pig.batches.core falls back to the same DEFAULT_BOAR_NAMES
  // seed PigContext used, because App's boot hydrates ppp-boars-v1
  // conditionally.
  const boarNames = batchesData?.boarNames ?? DEFAULT_BOAR_NAMES;
  // The readiness signal this page's record gate is keyed on. It replaces
  // PigContext.feedersLoaded and means the same thing it did — "the batch array
  // has actually resolved", never "the array happens to be empty" — but it is
  // now this surface's own settle rather than App's boot flag, and it can also
  // distinguish a FAILED read, which the boot flag could not.
  const feedersLoaded = !batchesResource.loading;
  // The hooks take a plain writer, not the raw state setter. Their mutations and
  // post-RPC reloads stay route-local; a later legacy-route transition refreshes
  // PigContext from the persisted blob through the regular boot path.
  const feederGroupsRef = React.useRef<PigFeederGroup[]>(feederGroups);
  feederGroupsRef.current = feederGroups;
  const localRevision = React.useRef(0);
  const setFeederGroups = (next: PigFeederGroup[]) => {
    ++localRevision.current;
    feederGroupsRef.current = next;
    setSavedFeederGroups(next as unknown as PigBatchRow[]);
  };
  // MIGRATION 237: this view no longer writes ppp-feeders-v1 as a whole
  // document. Its writes are audited server operations: batches, sub-batches
  // and mortality (231, applyFeederOp), the planned-trip auto-allocation and
  // the trip edits (237, pig_trip_operation), and the FCR cache (237).
  // The 231 operations (sub-batches, mortality, the feeder form) run on ONE
  // chain, so this view's own writes stay in order; each adopts the server's
  // element on a live result and re-reads on a replay, an unknown outcome or a
  // conflict.
  const feederWrites = React.useRef<Promise<unknown>>(Promise.resolve());
  const [applyFeederOp] = React.useState(() =>
    createFeederOpRunner({
      sb,
      chain: feederWrites,
      getGroups: () => feederGroupsRef.current,
      setGroups: (next) => setFeederGroups(next),
      reread: () => void batchesResource.refetch(),
      onSaveStatus: setSaveStatus,
    }),
  );
  // The feeder form. PigContext seeds it from a ten-key literal with no
  // `feedAllocatedToTransfers`; this view and the record page have always
  // written an eleventh key, and closeFeederForm reads it back.
  const feederForm = feederFormRaw as PigBatchFeederForm;
  const setFeederForm = setFeederFormRaw as unknown as React.Dispatch<React.SetStateAction<PigBatchFeederForm>>;
  const originalFeederForm = originalFeederFormRaw as PigBatchFeederForm | null;
  // PigContext types both open-record ids `unknown`; this view only ever sets
  // them to a row id or null.
  const editFeederId = editFeederIdRaw as string | null;
  const editTripId = editTripIdRaw as string | null;
  const activeTripBatchId = activeTripBatchIdRaw as string | null;
  // Sub-batch (partition) workflow — state + handlers live in usePigSubBatches
  // (CP8). partitionDirtyRef stays view-owned (closeFeederForm also reads it).
  const subBatches = usePigSubBatches({
    feederGroups,
    setFeederGroups,
    getFeederGroups: () => feederGroupsRef.current,
    applyFeederOp,
    reread: () => void batchesResource.refetch(),
    setNotice,
    confirmDelete,
    subAutoSaveTimer: subAutoSaveTimer as unknown as HookTimerRef,
    pigAutoSaveTimer: pigAutoSaveTimer as unknown as HookTimerRef,
    partitionDirtyRef,
  });
  // The hub Add-Batch flow + the feeder-form partition editor (both stay in this
  // view) use these two; the rest of the sub-batch API is consumed on the record
  // page via the bundle spread below.
  const {setShowSubForm} = subBatches;
  const updSubPartition: typeof subBatches.updSubPartition = (...args) => {
    if (!feederClosing.current) subBatches.updSubPartition(...args);
  };
  // Manager gate (admin OR management) — used by the planned-trip workflow plus
  // the Global ADG editor + planned-trip JSX. Computed here so the hook below
  // and the later JSX share one source.
  const isManager = !!(authState && (authState.role === 'admin' || authState.role === 'management'));
  // Planned-processing-trip workflow — state + lock sidecar + handlers live in
  // usePigPlannedTrips (CP9). Planned-trip JSX below consumes these. Mutations
  // route through the mig-176 SECDEF RPCs; setFeederGroups feeds the hook's
  // post-RPC reload of ppp-feeders-v1.
  const onTripsReloaded = () => {
    void batchesResource.refetch();
  };
  const plannedTrips = usePigPlannedTrips({
    feederGroups,
    setFeederGroups,
    setNotice,
    authState,
    isManager,
    onTripsReloaded,
  });
  // Processing-trip workflow — trip-source tracking + add/edit/close/delete live
  // in usePigProcessingTrips (CP10). Trip FORM state stays owned by PigContext
  // (threaded in). dailysForName is hoisted (function decl) so it's available here.
  const processingTrips = usePigProcessingTrips({
    feederGroups,
    setFeederGroups,
    setNotice,
    confirmDelete,
    tripAutoSaveTimer: tripAutoSaveTimer as unknown as HookTimerRef,
    breeders,
    dailysForName,
    activeTripBatchId,
    setActiveTripBatchId,
    tripForm,
    // The hook declares a plain writer; PigContext's setter is exactly that at
    // runtime, and this view never hands it an updater.
    setTripForm: setTripForm as unknown as (next: PigTripForm) => void,
    editTripId,
    setEditTripId,
    onTripsReloaded,
  });
  // The full pig daily history, read through pig.batches.core rather than off
  // useDailysRecent(). It is what dailysForName closes over, so every feed, FCR
  // and current-head number on both surfaces is derived from it.
  const pigDailys = (batchesData?.pigDailys || []) as unknown as PigBatchesDailyRow[];
  const {setView} = useUI();
  const navigate = useNavigate();
  const location = useLocation() as PigBatchesLocation;
  // Record-page routing: /pig/batches/<encodeURIComponent(group.id)> renders the
  // single selected batch's full workspace; the bare hub renders nav-only tiles.
  // group.id is the stable route identity; batchName stays the display label.
  const recordMode = location.pathname.startsWith('/pig/batches/');
  // decode in a try/catch — a malformed %-sequence (e.g. /pig/batches/%) would
  // otherwise throw; fall back to the raw slice so it just resolves to
  // not-found instead of crashing the view (mirrors BroilerBatchPage).
  const recordRawId = recordMode ? location.pathname.slice('/pig/batches/'.length) : NO_RECORD_ID;
  let recordId = recordRawId;
  if (recordMode) {
    try {
      recordId = decodeURIComponent(recordRawId);
    } catch {
      recordId = recordRawId;
    }
  }
  const recordGroup = recordMode ? (feederGroups || []).find((g) => g.id === recordId) : null;
  // Originating list order handed through route state; absent on direct links.
  const recordSeq = location.state?.recordSeq || null;
  // Per-batch grid metrics keyed by group.id.
  // These need breeders + dailys, so the pure filter lib can't derive them — the
  // view computes them once here and threads the map through `ctx` so the
  // started/current/feedPerStarted SORTS agree with what each row renders.
  const pigBatchMetricsById = React.useMemo(() => {
    const map: Record<string, PigBatchGridMetrics> = {};
    for (const g of feederGroups || []) {
      const metrics = buildPigBatchGridMetrics(g, {
        breeders,
        dailysForName,
        tripSourceSummary: processingTrips.tripSourceSummary,
      });
      map[g.id] = {...metrics, feedPerStarted: metrics.feedPerPig};
    }
    return map;
    // breeders / dailys / processingTrips feed the ledger + feed math; recompute
    // when any change. dailysForName closes over pigDailys (in deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feederGroups, breeders, pigDailys, processingTrips.tripSourceSummary]);
  const pigBatchFilterCtx = {metricsById: pigBatchMetricsById};
  // Q07 removes the hidden filter toolbar. Keep the same default status order;
  // complete rows stay available behind the remembered C14 fold.
  const visiblePigBatches = (feederGroups || [])
    .slice()
    .sort(buildPigBatchComparator({key: 'status', dir: 'asc'}, pigBatchFilterCtx));
  const sequencePigBatches = visiblePigBatches.filter((group) => showArchBatches || group.status !== 'processed');
  const goToBatch = (id: string, rows?: PigBatchRow[]) =>
    navigate(
      '/pig/batches/' + encodeURIComponent(id),
      rows ? recordSeqNavOptions(labeledSeqItems(rows, 'batchName')) : undefined,
    );
  // `any` for the same reason as SowsView's navigateSeq — see the note there.
  const navigateSeq = (id: any) => navigate('/pig/batches/' + encodeURIComponent(id), recordSeqNavOptions(recordSeq));
  const goToHub = () => navigate('/pig/batches');

  const cycleSeqMap = buildCycleSeqMap(breedingCycles);
  // Processing-trip source tracking + handlers live in usePigProcessingTrips
  // (CP10); tripSourceCounts is consumed by the trip JSX via the hook.

  // ── Planned-trip forecasting infrastructure (commit 4a) ─────────────────
  // Global ADG: app_store key ppp-pig-global-adg-v1, shape
  //   {manualValue: number | null, updatedAt: ISO | null,
  //    updatedBy: profileId | null}
  // Manual value (when set) overrides the system estimate. System estimate
  // is computed live from pig sessions with usable (ageDays, avgWeightLbs).
  // No reset button per Codex — admin types a new value to change. The
  // null-clearing path (revert to live seed) is parked for a future
  // discussion.
  const [globalAdgRow, setGlobalAdgRow] = React.useState<GlobalAdgRow | null>(null); // null=loading, {} after load
  const [adgEditing, setAdgEditing] = React.useState(false);
  const [adgInput, setAdgInput] = React.useState('');
  const [adgSaving, setAdgSaving] = React.useState(false);
  // Planned-trip state + lock sidecar live in usePigPlannedTrips (CP9).
  React.useEffect(() => {
    (
      loadPigGlobalAdg(sb)
        // `.then()` on a PostgrestBuilder is DECLARED to return PromiseLike, which
        // has no `.catch`; the runtime value is a real Promise, which is what the
        // existing chain has always relied on.
        .then(({data, error}) => {
          if (error) {
            setGlobalAdgRow({manualValue: null, updatedAt: null, updatedBy: null});
            setNotice({kind: 'warning', message: 'Global ADG override could not be loaded: ' + error.message});
            return;
          }
          if (data && data.data && typeof data.data === 'object') {
            setGlobalAdgRow(data.data as GlobalAdgRow);
          } else {
            setGlobalAdgRow({manualValue: null, updatedAt: null, updatedBy: null});
          }
        }) as Promise<void>
    ).catch((e) => {
      setGlobalAdgRow({manualValue: null, updatedAt: null, updatedBy: null});
      setNotice({kind: 'warning', message: 'Global ADG override could not be loaded: ' + (e?.message || e)});
    });
  }, []);

  // Latest pig weigh-in session entries grouped by sub_batch_id (resolved
  // via pigSlug from session.batch_id). Drives the rank-window projection
  // when latest entries exist, and also feeds the system-estimate ADG seed.
  const [pigSessionsForForecast, setPigSessionsForForecast] = React.useState<PigForecastSession[]>([]);
  const [pigEntriesForForecast, setPigEntriesForForecast] = React.useState<PigForecastEntry[]>([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {data: sessions, error: sessionsError} = await loadPigForecastSessions(sb);
        if (sessionsError) throw new Error('weigh_in_sessions: ' + sessionsError.message);
        if (cancelled) return;
        setPigSessionsForForecast(sessions || []);
        const ids = (sessions || []).map((s) => s.id);
        if (ids.length === 0) {
          setPigEntriesForForecast([]);
          return;
        }
        const {data: ents, error: entriesError} = await loadPigForecastEntries(sb, ids);
        if (entriesError) throw new Error('weigh_ins: ' + entriesError.message);
        if (cancelled) return;
        setPigEntriesForForecast(ents || []);
      } catch (e) {
        if (cancelled) return;
        setPigSessionsForForecast([]);
        setPigEntriesForForecast([]);
        setNotice({
          kind: 'warning',
          message: 'Pig forecast weigh-ins could not be loaded: ' + ((e as {message?: string})?.message || e),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // System ADG estimate via seedGlobalADG. Pairs each pig session's avg
  // weight with its age in days at session date. Age = sessionDate − cycle's
  // first farrowing date (or theoretical farrowingStart fallback).
  const systemAdgEstimate = React.useMemo(() => {
    if (pigSessionsForForecast.length === 0) return null;
    const sessAvg: Record<string, {sum: number; n: number}> = {};
    for (const e of pigEntriesForForecast) {
      const w = parseFloat(e.weight as string);
      if (!isFinite(w) || w <= 0) continue;
      if (!sessAvg[e.session_id as string]) sessAvg[e.session_id as string] = {sum: 0, n: 0};
      sessAvg[e.session_id as string].sum += w;
      sessAvg[e.session_id as string].n += 1;
    }
    const usable: {ageDays: number; avgWeightLbs: number}[] = [];
    for (const s of pigSessionsForForecast) {
      const ent = sessAvg[s.id];
      if (!ent || ent.n === 0) continue;
      // Resolve cycle for this session via feederGroups.
      const slug = pigSlug(s.batch_id);
      // pigSlug takes `unknown` — batch_id is forwarded exactly as loaded.
      let cycleId: string | null | undefined = null;
      for (const g of feederGroups || []) {
        for (const sub of g.subBatches || []) {
          if (pigSlug(sub.name) === slug) {
            cycleId = g.cycleId;
            break;
          }
        }
        if (cycleId) break;
      }
      if (!cycleId) continue;
      const ageRange = libCalcAgeRange(cycleId, new Date(s.date + 'T12:00:00'), breedingCycles, farrowingRecs);
      if (ageRange.minDays == null || ageRange.maxDays == null) continue;
      // Use the midpoint of the cycle age range.
      const ageDays = (ageRange.minDays + ageRange.maxDays) / 2;
      usable.push({ageDays, avgWeightLbs: ent.sum / ent.n});
    }
    return seedGlobalADG(usable);
  }, [feederGroups, breedingCycles, farrowingRecs, pigSessionsForForecast, pigEntriesForForecast]);

  // Effective Global ADG: manual value when set, otherwise the live system
  // estimate. null when neither is available.
  const effectiveAdgLbsPerDay =
    globalAdgRow && globalAdgRow.manualValue != null
      ? parseFloat(globalAdgRow.manualValue as string)
      : systemAdgEstimate
        ? systemAdgEstimate.valueLbsPerDay
        : null;

  function persistGlobalAdg(nextValue: number | string | null) {
    setAdgSaving(true);
    const next = {
      manualValue: nextValue,
      updatedAt: new Date().toISOString(),
      updatedBy: (authState && authState.user && authState.user.id) || null,
    };
    persistPigGlobalAdg(sb, next).then(({error}) => {
      setAdgSaving(false);
      if (error) {
        console.warn('persistGlobalAdg error:', error.message || error);
        setNotice({kind: 'error', message: 'Global ADG save failed: ' + (error.message || String(error))});
        return;
      }
      setGlobalAdgRow(next);
      setAdgEditing(false);
    });
  }

  // ── Planned-trip lock helpers (Codex pig planned trips lane) ────────────
  // Locked trips block MANUAL /pig/batches edits end-to-end — guards live
  // INSIDE the handlers, not only in JSX, so neighbor moves and chain-
  // additions also respect the lock. Send-to-Trip fulfillment from
  // /pig/weighins is intentionally NOT gated by these locks: when an
  // operator sends pigs to a planned trip, the fulfillment flow may
  // reconcile a locked trip's plannedCount (decrement, or remove the
  // trip outright if all its planned pigs are processed) per the
  // existing planned/processing trip contract. The lock only forbids
  // manual date / count / add / delete mutation in this view — the
  // processor date a trip was scheduled with stays intact through
  // fulfillment.
  // Planned-trip lock helpers + add/move/delete/date handlers live in
  // usePigPlannedTrips (CP9). The planned-trip JSX consumes them via the hook.

  const [allocationFailed, setAllocationFailed] = React.useState(false);
  const [allocationRetry, setAllocationRetry] = React.useState(0);
  // Auto-allocate planned trips for any (sub, sex) pair that satisfies all
  // requirements (Codex Q2). Idempotent and narrow: never overwrites an
  // existing pair and never writes when requirements are missing.
  React.useEffect(() => {
    if (!feederGroups || feederGroups.length === 0) return;
    if (effectiveAdgLbsPerDay == null) return;
    const today = new Date().toISOString().slice(0, 10);
    let dirty = false;
    const allocatedGroupIds: string[] = [];
    // What the forecast decided, sent as ONE audited operation (migration 237).
    const allocations: PigPlannedTripAllocation[] = [];
    const next = feederGroups.map((g) => {
      if (g.status === 'processed' || !g.cycleId) return g;
      const subs = g.subBatches || [];
      if (subs.length === 0) return g;
      const ageRange = libCalcAgeRange(g.cycleId, new Date(today + 'T12:00:00'), breedingCycles, farrowingRecs);
      if (ageRange.minDays == null || ageRange.maxDays == null) return g;
      const cycleAgeDaysAtRef = {minDays: ageRange.minDays, maxDays: ageRange.maxDays};
      const ageDistributionAtRef = buildFarrowingAgeDistribution({
        cycleId: g.cycleId,
        asOfDate: today,
        breedingCycles,
        farrowingRecs,
        cycleAgeDaysAtRef,
      });
      const planned = Array.isArray(g.plannedProcessingTrips) ? g.plannedProcessingTrips.slice() : [];
      let groupDirty = false;
      for (const sub of subs) {
        if (sub.status === 'processed') continue;
        const giltCount = parseInt(sub.giltCount as string) || 0;
        const boarCount = parseInt(sub.boarCount as string) || 0;
        // Sex-mixed subs are flagged in the UI; never auto-allocate (Q1).
        if (giltCount > 0 && boarCount > 0) continue;
        const sex = giltCount > 0 ? 'gilt' : boarCount > 0 ? 'boar' : null;
        if (!sex) continue;
        // Already has planned trips for this (sub, sex) pair — leave alone.
        const existingForPair = planned.filter((t) => t.subBatchId === sub.id && t.sex === sex);
        if (existingForPair.length > 0) continue;
        // Ledger remaining for this sub. Mortality + transfers + real-trip
        // attributions are sex-agnostic in current data; for v1 we treat
        // a single-sex sub's remaining as the whole ledger remainder.
        const transfers = pigTransfersForSub(breeders, g.batchName, sub.name);
        const tripPigs = pigTripPigsForSub(g.processingTrips || [], sub.id, {
          tripSourceSummary: processingTrips.tripSourceSummary,
          subName: sub.name,
        });
        const mortality = pigMortalityForSub(g, sub.name);
        const started = giltCount + boarCount;
        const remaining = Math.max(0, started - tripPigs - transfers.count - mortality);
        if (remaining <= 0) continue;
        // Forecasted ready start date. Ronnie preference: planned trip dates
        // are based only on DOB/farrowing age at trip date times Global ADG.
        // First weigh-ins do not move planned dates.
        const adg = effectiveAdgLbsPerDay;
        if (adg == null) continue;
        const forecastPopulationCount = Math.max(0, started - transfers.count - mortality);
        const nextWindow = projectFarrowingAgeWindow(ageDistributionAtRef, {
          populationCount: forecastPopulationCount,
          rankOffset: tripPigs,
          windowCount: 1,
        });
        const anchorAgeDays = nextWindow ? nextWindow.maxDays : ageRange.maxDays;
        const anchorWeight = anchorAgeDays * adg;
        const anchorDate = today;
        let readyDate = today;
        if (anchorWeight != null && adg > 0) {
          const daysToReady = Math.max(0, (PLANNED_TRIP_TARGET_WEIGHT_LBS - anchorWeight) / adg);
          const d = new Date(anchorDate + 'T12:00:00');
          d.setDate(d.getDate() + Math.ceil(daysToReady));
          const candidate = d.toISOString().slice(0, 10);
          readyDate = candidate < today ? today : candidate;
        }
        const allocated = allocatePlannedTrips({
          remainingCount: remaining,
          sex,
          subBatchId: sub.id as string,
          startDate: readyDate,
        });
        if (allocated.length > 0) {
          for (const t of allocated) {
            planned.push(t);
            // allocatePlannedTrips computes plannedCount as an integer; the
            // type is the stored-trip type, which also admits a legacy string.
            if (g.id) {
              allocations.push({
                groupId: g.id,
                subBatchId: t.subBatchId,
                sex: t.sex,
                date: t.date,
                plannedCount: Number(t.plannedCount),
              });
            }
          }
          groupDirty = true;
        }
      }
      if (groupDirty) {
        dirty = true;
        if (g.id) allocatedGroupIds.push(g.id);
        return {...g, plannedProcessingTrips: planned};
      }
      return g;
    });
    if (dirty && allocations.length > 0) {
      // MIGRATION 237: the computed trips go to the server as ONE operation
      // (trip.add_planned_batch) instead of a whole ppp-feeders-v1 upsert. The
      // server re-checks, under the feeders lock, that each (sub-batch, sex)
      // pair still has no planned trip -- so two tabs allocating at once cannot
      // double a pair -- mints the ids, and writes the batch's Activity row.
      void pigAddPlannedTripsBatch(sb, allocations).then(
        () => {
          setAllocationFailed(false);
          // This runs only after the operation commits. A number sync failure
          // must never replay that already committed write.
          void (async () => {
            let failed = false;
            for (const groupId of allocatedGroupIds) {
              try {
                await pigSyncProcessingTripNumbers(sb, groupId);
              } catch (error) {
                failed = true;
                console.warn('Planned trip number refresh:', error);
              }
            }
            if (failed) {
              setNotice({kind: 'error', message: 'Planned trips saved, but some trip numbers could not be refreshed.'});
            }
            await batchesResource.refetch();
          })().catch((error) => {
            console.warn('Planned trip resource refresh:', error);
          });
        },
        (error) => {
          console.warn('Planned trip allocation:', error);
          setAllocationFailed(true);
        },
      );
    } else {
      setAllocationFailed(false);
    }
    void next;
    // sb is a stable prop; the allocation reads from the latest feederGroups
    // through the closure. eslint-disable matches the file's existing
    // pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feederGroups, breedingCycles, farrowingRecs, breeders, effectiveAdgLbsPerDay, allocationRetry]);

  // ── Pig mortality entries (parent batch with sub-batch attribution) ─────
  // Stored as feederGroup.pigMortalities = [{id, date, sub_batch_id,
  // sub_batch_name, count, comment, team_member, created_at}]. Pure audit
  // log — current pig count keeps coming from dailys; mortality just
  // surfaces the death history with attribution. State + handlers live in
  // usePigMortality (CP7); the modal + mortality-log JSX render in PigBatchPage.
  const mortality = usePigMortality({feederGroups, applyFeederOp, setNotice, authState});

  // Match pig_dailys to a name (case-insensitive) — used for both batch and sub-batch matching
  function dailysForName(name: string | null | undefined) {
    if (!name) return [];
    const n = String(name).trim().toLowerCase();
    // Also build a slug version for fallback matching
    const slug = n.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return pigDailys.filter((d) => {
      const lbl = (d.batch_label || '').trim().toLowerCase();
      const bid = (d.batch_id || '').trim().toLowerCase();
      const bidSlug = bid.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const lblSlug = lbl.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return lbl === n || bid === n || bidSlug === slug || lblSlug === slug;
    });
  }

  function renderPigBatchTile(g: PigBatchRow, rowsForSequence: PigBatchRow[]) {
    const metrics =
      pigBatchMetricsById[g.id] ||
      buildPigBatchGridMetrics(g, {breeders, dailysForName, tripSourceSummary: processingTrips.tripSourceSummary});
    return <PigBatchHubTile key={g.id} group={g} metrics={metrics} onOpen={() => goToBatch(g.id, rowsForSequence)} />;
  }

  // Archive / reopen through pig_apply_operation (231): archive marks the batch
  // AND every sub-batch processed in one transaction; reopen is the batch only.
  // The server logs the pig.batch status.changed Activity in that transaction.
  function feederFailure(outcome: PigOpOutcome, prefix: string): string | null {
    if (outcome.status === 'rejected') return prefix + pigRejectionMessage(outcome);
    if (outcome.status === 'unknown') return prefix + outcome.message;
    if (outcome.status === 'conflict') return prefix + 'this batch changed elsewhere. Reload and try again.';
    return null;
  }
  async function setBatchStatus(batchId: string, operation: 'feeder.archive' | 'feeder.reopen') {
    const outcome = await applyFeederOp({requestId: newPigRequestId(), operation, target: {feeder_id: batchId}});
    const failed = feederFailure(outcome, operation === 'feeder.archive' ? 'Not archived: ' : 'Not reopened: ');
    if (failed) setNotice({kind: 'error', message: failed});
    return !failed;
  }
  function archiveBatch(batchId: string) {
    window._wcfConfirm?.(
      'Mark this batch complete? It will be hidden from the webform.',
      async () => {
        await setBatchStatus(batchId, 'feeder.archive');
      },
      'Mark Complete',
    );
  }
  async function unarchiveBatch(batchId: string) {
    await setBatchStatus(batchId, 'feeder.reopen');
  }
  // Sub-batch CRUD (archive/unarchive/persist/validate/upd/close/save/delete)
  // lives in usePigSubBatches (CP8); the form JSX below consumes the handlers.

  // Thin closure over the lib helper that supplies the React-context-bound
  // breedingCycles + farrowingRecs arrays. Keeping the wrapper preserves the
  // existing call-site signature (cycleId, asOfDate?) without ripple changes.
  function calcAgeRange(cycleId: string | null | undefined, asOfDate?: Date | null) {
    return libCalcAgeRange(cycleId, asOfDate, breedingCycles, farrowingRecs);
  }

  // Trip yield helpers (parseLiveWeights / tripTotalLive / tripYield) now live
  // in lib/pig.js as pure helpers, shared with computePigBatchFCR.

  // Processing-trip handlers (persistTrip/updTrip/closeTripForm/deleteTrip) live
  // in usePigProcessingTrips (CP10); the trip form + list JSX consume them.

  // updSubPartition (the in-modal partition-count editor) also lives in
  // usePigSubBatches (CP8); it shares partitionDirtyRef with closeFeederForm.

  const feederClosing = React.useRef(false);
  const newFeederId = React.useRef<string | null>(null);
  const [feederBusy, setFeederBusy] = React.useState(false);
  // ---- The feeder form through pig_apply_operation (231) -------------------
  // Each confirmed autosave is ONE feeder.update of the fields whose meaning
  // changed, with their expected-before values read off the PERSISTED row
  // (legacy batches store counts as strings; "5" and 5 mean the same count and
  // are not an edit). The server logs one pig.batch field.updated per save in
  // that save's transaction — coordinator ruling Q1 — so the form emits no
  // Activity of its own, not even a close summary. Status goes through
  // feeder.archive / feeder.reopen (ruling Q3); originalPigCount is derived by
  // the server whenever a count changes.
  const feederFormRef = React.useRef(feederForm);
  feederFormRef.current = feederForm;
  const feederUpdateRequest = React.useRef<{intent: string; requestId: string} | null>(null);
  const [feederConflict, setFeederConflict] = React.useState<PigConflictState | null>(null);
  function adoptLatestFeeder(id: string, conflicts: PigConflict[], latest: PigFeederGroup | null) {
    void batchesResource.refetch();
    const row = latest ?? feederGroupsRef.current.find((g) => g.id === id) ?? null;
    setFeederConflict(null);
    if (!row) return;
    // Take the other person's value for each conflicting field; keep every
    // other edit in the draft and send it again against the new values.
    const merged = {...feederFormRef.current} as Record<string, unknown>;
    for (const {field} of conflicts) merged[field] = feederFormValue(field, row[field as keyof PigFeederGroup]);
    merged.originalPigCount = (parseInt(String(merged.giltCount)) || 0) + (parseInt(String(merged.boarCount)) || 0);
    const form = merged as PigBatchFeederForm;
    setFeederForm(form);
    feederAutosave.update({id, form});
  }
  async function persistFeederDraft({id, form}: {id: string; form: PigBatchFeederForm}) {
    const row = feederGroupsRef.current.find((g) => g.id === id);
    if (!row) {
      setNotice({kind: 'error', message: 'This batch is no longer available. Your edits have not been saved.'});
      return false;
    }
    const next = feederChangeFrom(form);
    const fields = FEEDER_EDIT_FIELDS.filter(
      (field) => !sameFeederValue(field, (row as Record<string, unknown>)[field], next[field]),
    );
    if (fields.length === 0) {
      setOriginalFeederForm(form);
      return true;
    }
    const change = Object.fromEntries(fields.map((field) => [field, next[field]]));
    const expected = expectedFrom(row as Record<string, unknown>, fields);
    // A retry of the SAME change reuses its request id (the server replays it).
    const intent = JSON.stringify([id, expected, change]);
    const prior = feederUpdateRequest.current;
    const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
    feederUpdateRequest.current = {intent, requestId};
    const outcome = await applyFeederOp({
      requestId,
      operation: 'feeder.update',
      target: {feeder_id: id},
      expected,
      change,
    });
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      feederUpdateRequest.current = null;
      setFeederConflict(null);
      setOriginalFeederForm(form);
      return true;
    }
    if (outcome.status === 'conflict') {
      feederUpdateRequest.current = null;
      const latest = (outcome.rows?.feeder as PigFeederGroup | undefined) ?? null;
      setFeederConflict({
        conflicts: outcome.conflicts,
        labels: PIG_BATCH_FIELD_LABELS,
        useLatest: async () => adoptLatestFeeder(id, outcome.conflicts, latest),
      });
      return false;
    }
    setNotice({kind: 'error', message: feederFailure(outcome, 'Not saved: ') || 'Not saved.'});
    return false;
  }
  const persistFeederDraftRef = React.useRef(persistFeederDraft);
  persistFeederDraftRef.current = persistFeederDraft;
  const [feederAutosave] = React.useState(() =>
    createConfirmedDraftAutosave<{id: string; form: PigBatchFeederForm}>({
      debounceMs: 1500,
      validate: ({form}) => !!form.batchName.trim(),
      persist: (candidate) => persistFeederDraftRef.current(candidate),
      onState: () => {},
    }),
  );
  React.useLayoutEffect(() => {
    feederAutosave.resume();
    return () => feederAutosave.suspend();
  }, [feederAutosave]);
  useAppDraftGuard({
    ownerKey: 'pig-feeder-draft',
    isUnsafeToLeave: () =>
      feederAutosave.isDirty() ||
      partitionDirtyRef.current ||
      feederClosing.current ||
      (!!showFeederForm && !editFeederId),
    settleBeforeLeave: async () => {
      if (feederClosing.current || (showFeederForm && !editFeederId)) return false;
      if (!(await feederAutosave.flush())) return false;
      return subBatches.flushPartition();
    },
  });
  // The Status select is not a field edit: it archives (cascading to every
  // sub-batch, exactly like Mark Complete) or reopens the batch at once.
  async function changeFeederStatus(id: string, status: string) {
    const before = feederFormRef.current.status;
    if (status === before) return;
    setFeederForm({...feederFormRef.current, status});
    const ok = await setBatchStatus(id, status === 'processed' ? 'feeder.archive' : 'feeder.reopen');
    if (ok) {
      setOriginalFeederForm({...(originalFeederForm || feederFormRef.current), status} as PigBatchFeederForm);
    } else {
      setFeederForm({...feederFormRef.current, status: before});
    }
  }
  function updFeeder(k: keyof PigBatchFeederForm, v: string) {
    if (feederClosing.current) return;
    if (k === 'status' && editFeederId) {
      void changeFeederStatus(editFeederId, v);
      return;
    }
    const f = {...feederForm, [k]: v} as PigBatchFeederForm;
    if (k === 'giltCount' || k === 'boarCount')
      f.originalPigCount =
        (parseInt((k === 'giltCount' ? v : f.giltCount) as string) || 0) +
        (parseInt((k === 'boarCount' ? v : f.boarCount) as string) || 0);
    setFeederForm(f);
    if (editFeederId) feederAutosave.update({id: editFeederId, form: f});
  }
  async function closeFeederForm() {
    if (feederClosing.current) return;
    feederClosing.current = true;
    setFeederBusy(true);
    try {
      clearTimeout(pigAutoSaveTimer.current ?? undefined);
      setNotice(null);
      if (editFeederId) {
        // The last revision, then any partition edits (decreases first).
        feederAutosave.update({id: editFeederId, form: feederFormRef.current});
        if (!(await feederAutosave.flush())) return;
        if (!(await subBatches.flushPartition())) return;
      }
      setFeederConflict(null);
      setShowFeederForm(false);
      setEditFeederId(null);
      setOriginalFeederForm(null);
      newFeederId.current = null;
    } finally {
      feederClosing.current = false;
      setFeederBusy(false);
    }
  }

  // Add Batch: feeder.create with the id and request id made once per open
  // form, so a retry after a lost reply cannot add the batch twice. The server
  // writes the Add form's defaults, derives the started head and logs the
  // record.created Activity.
  const feederCreateRequest = React.useRef<{intent: string; requestId: string} | null>(null);
  async function createFeeder() {
    if (feederClosing.current) return;
    setNotice(null);
    if (!feederForm.batchName || !feederForm.batchName.trim()) {
      setNotice({kind: 'error', message: 'Please enter a batch name.'});
      return;
    }
    const id = (newFeederId.current ??= String(Date.now()));
    const typed = feederChangeFrom(feederForm as unknown as Record<string, unknown>);
    const change = {
      batchName: typed.batchName,
      giltCount: typed.giltCount,
      boarCount: typed.boarCount,
      startDate: typed.startDate,
      notes: typed.notes,
    };
    const intent = JSON.stringify([id, change]);
    const prior = feederCreateRequest.current;
    const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
    feederCreateRequest.current = {intent, requestId};
    feederClosing.current = true;
    setFeederBusy(true);
    const outcome = await applyFeederOp({requestId, operation: 'feeder.create', target: {feeder_id: id}, change});
    feederClosing.current = false;
    setFeederBusy(false);
    const alreadyThere = outcome.status === 'rejected' && outcome.code === '23505';
    if (outcome.status !== 'saved' && !alreadyThere) {
      setNotice({kind: 'error', message: feederFailure(outcome, 'Not added: ') || 'Not added.'});
      return;
    }
    if (alreadyThere) void batchesResource.refetch();
    feederCreateRequest.current = null;
    newFeederId.current = null;
    setShowFeederForm(false);
    setEditFeederId(null);
    setOriginalFeederForm(null);
  }
  // Delete: feeder.delete; the server logs record.deleted with the batch's
  // sub-batch, trip and mortality counts.
  function deleteFeeder(id: string) {
    confirmDelete('Delete this batch? This cannot be undone.', async () => {
      if (feederClosing.current || !(await feederAutosave.flush())) return;
      feederClosing.current = true;
      setFeederBusy(true);
      clearTimeout(pigAutoSaveTimer.current ?? undefined);
      const outcome = await applyFeederOp({
        requestId: newPigRequestId(),
        operation: 'feeder.delete',
        target: {feeder_id: id},
      });
      feederClosing.current = false;
      setFeederBusy(false);
      const failed = feederFailure(outcome, 'Not deleted: ');
      if (failed) {
        setNotice({kind: 'error', message: failed});
        return;
      }
      setFeederConflict(null);
      setShowFeederForm(false);
      setEditFeederId(null);
      setOriginalFeederForm(null);
    });
  }

  // isManager (admin/management mutation gate) is computed once near the top,
  // alongside the usePigPlannedTrips hook that consumes it.

  // CP11: the record-page workspace (card + mortality modal + collaboration)
  // renders in PigBatchPage. PigBatchesView owns the hooks + view-level
  // derivations and threads them down as one bundle so the moved JSX is
  // byte-identical. The hub (ADG / Add Batch / archived toggle / tiles), the
  // shared feeder-form modal, and the back/loading/not-found states stay here.
  // The batch form's field block, unchanged from the modal it used to fill:
  // the Add modal and the record's Batch Details card (R25) both render it, so
  // both bind the same updFeeder / updSubPartition / changeFeederStatus paths.
  function renderFeederFields() {
    return (
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
        <div style={{gridColumn: '1/-1'}}>
          <label style={S.label}>
            Batch name <span style={{color: 'var(--danger-ink)'}}>*</span>
          </label>
          <input
            value={feederForm.batchName}
            onChange={(e) => updFeeder('batchName', e.target.value)}
            placeholder="e.g. Group 2 Fall 2025"
          />
        </div>
        {/* Farm-born batches are created from the first farrowing record
              (FarrowingView), not here. Add Manual Batch offers no cycle
              selector; in Edit mode the linked cycle is read-only/fixed. */}
        {editFeederId && feederForm.cycleId && (
          <div>
            <label style={S.label}>Linked breeding cycle (fixed)</label>
            <select
              value={feederForm.cycleId}
              onChange={(e) => updFeeder('cycleId', e.target.value)}
              data-feeder-cycle-select
              disabled
            >
              <option value="">{'\u2014 Select \u2014'}</option>
              {breedingCycles
                .filter((c) => {
                  // Commit 5 — hide cycles already linked to ANOTHER
                  // pig batch. Edit mode keeps the self-batch's
                  // cycle visible by excluding self from the link
                  // set. Empty cycleId entries are ignored.
                  if (!editFeederId) {
                    // Add mode: any other batch linking this cycle hides it.
                    return !(feederGroups || []).some((fg) => fg.cycleId === c.id);
                  }
                  // Edit mode: only exclude OTHER batches.
                  return !(feederGroups || []).some((fg) => fg.id !== editFeederId && fg.cycleId === c.id);
                })
                .map((c) => {
                  const tl = calcBreedingTimeline(c.exposureStart);
                  return (
                    <option key={c.id} value={c.id as string}>
                      {cycleLabel(c, cycleSeqMap)} —{' '}
                      {tl ? fmtS(tl.farrowingStart) + ' to ' + fmtS(tl.farrowingEnd) : fmtS(c.exposureStart)}
                    </option>
                  );
                })}
            </select>
            {/* Commit 5 empty-state hint: shown only in Add mode
              when every breeding cycle is already linked. Edit
              mode never hits this branch because the self batch's
              own cycle stays visible. */}
          </div>
        )}
        <div>
          <label style={S.label}>
            Status <span style={{color: 'var(--danger-ink)'}}>*</span>
          </label>
          <select value={feederForm.status} onChange={(e) => updFeeder('status', e.target.value)}>
            {['active', 'processed'].map((s) => (
              <option key={s} value={s}>
                {processingStatusLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={S.label}>
            Start date <span style={{color: 'var(--danger-ink)'}}>*</span>
          </label>
          <input
            type="date"
            value={feederForm.startDate || ''}
            onChange={(e) => updFeeder('startDate', e.target.value)}
          />
        </div>
        <div>
          <label style={S.label}>
            Gilts count <span style={{color: 'var(--danger-ink)'}}>*</span>
          </label>
          <input
            type="number"
            min="0"
            value={feederForm.giltCount || ''}
            onChange={(e) => updFeeder('giltCount', e.target.value)}
          />
        </div>
        <div>
          <label style={S.label}>
            Boars count (intact males) <span style={{color: 'var(--danger-ink)'}}>*</span>
          </label>
          <input
            type="number"
            min="0"
            value={feederForm.boarCount || ''}
            onChange={(e) => updFeeder('boarCount', e.target.value)}
          />
        </div>
        <div style={{gridColumn: '1/-1'}}>
          <label style={S.label}>Original pig count</label>
          <div
            style={{
              padding: '8px 11px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--ink)',
            }}
          >
            {(parseInt(feederForm.giltCount as string) || 0) + (parseInt(feederForm.boarCount as string) || 0)}
          </div>
          <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 2}}>Auto-calculated: gilts + boars</div>
        </div>
        {editFeederId &&
          (() => {
            const liveGroup = feederGroups.find((x) => x.id === editFeederId);
            const subs = (liveGroup && liveGroup.subBatches) || [];
            if (subs.length === 0) return null;
            const sumG = subs.reduce((s, sb) => s + (parseInt(sb.giltCount as string) || 0), 0);
            const sumB = subs.reduce((s, sb) => s + (parseInt(sb.boarCount as string) || 0), 0);
            const tgtG = parseInt(feederForm.giltCount as string) || 0;
            const tgtB = parseInt(feederForm.boarCount as string) || 0;
            const okG = sumG === tgtG;
            const okB = sumB === tgtB;
            return (
              <div
                style={{
                  gridColumn: '1/-1',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: 'var(--surface-2)',
                }}
              >
                <div style={{fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginBottom: 6}}>
                  Distribute across sub-batches
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                  {subs.map((sb) => {
                    const isBoars =
                      (parseInt(sb.boarCount as string) || 0) > 0 && (parseInt(sb.giltCount as string) || 0) === 0;
                    const sex = isBoars ? 'Boars' : 'Gilts';
                    const c = isBoars ? parseInt(sb.boarCount as string) || 0 : parseInt(sb.giltCount as string) || 0;
                    return (
                      <div
                        key={sb.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 80px 100px',
                          gap: 8,
                          alignItems: 'center',
                        }}
                      >
                        <span style={{fontSize: 12, color: 'var(--ink)', fontWeight: 600}}>{sb.name}</span>
                        <span
                          style={{
                            fontSize: 11,
                            padding: '3px 8px',
                            textAlign: 'center',
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                          }}
                        >
                          {sex}
                        </span>
                        <input
                          type="number"
                          min="0"
                          value={c || ''}
                          onChange={(e) => updSubPartition(editFeederId, sb.id as string, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 14,
                    marginTop: 8,
                    padding: '8px 10px',
                    fontSize: 12,
                    color: 'var(--ink)',
                    border: `1px solid ${okG && okB ? 'var(--border)' : 'var(--danger)'}`,
                    borderRadius: 12,
                  }}
                >
                  <span style={{fontWeight: 600}}>
                    Allocated {sumG} of {tgtG} Gilts
                  </span>
                  <span style={{fontWeight: 600}}>
                    Allocated {sumB} of {tgtB} Boars
                  </span>
                </div>
                {(!okG || !okB) && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: 'var(--danger-ink)',
                    }}
                  >
                    Adjust the sub-batch counts to match the batch’s gilts and boars.
                  </div>
                )}
              </div>
            );
          })()}
        <div style={{gridColumn: '1/-1'}}>
          <label style={S.label}>
            Feed credited to breeding transfers (lbs){' '}
            <span style={{fontWeight: 400, color: 'var(--ink-faint)', fontSize: 11}}>
              {'(subtracted from total feed; set to 0 to clear)'}
            </span>
          </label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={feederForm.feedAllocatedToTransfers || ''}
            onChange={(e) => updFeeder('feedAllocatedToTransfers', e.target.value)}
            placeholder="0"
          />
        </div>
        <div style={{gridColumn: '1/-1'}}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--ink-muted)',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            {'\ud83d\udcb0 FEED COST RATE'}{' '}
            <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>
              {'(locked \u2014 set in Admin \u203a Feed Costs)'}
            </span>
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink)',
              padding: '8px 12px',
              background: 'var(--surface-2)',
              borderRadius: 10,
              border: '1px solid var(--border)',
            }}
          >
            Pig feed:{' '}
            <strong>
              {feederForm.perLbFeedCost !== '' && feederForm.perLbFeedCost != null
                ? '$' + parseFloat(feederForm.perLbFeedCost as string).toFixed(3) + '/lb'
                : '\u2014'}
            </strong>
          </div>
        </div>

        {feederForm.cycleId && (
          <div
            style={{
              gridColumn: '1/-1',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 12,
              color: 'var(--text-primary)',
            }}
          >
            <strong>Age range today:</strong> {calcAgeRange(feederForm.cycleId).text}
          </div>
        )}
        <div style={{gridColumn: '1/-1'}}>
          <label style={S.label}>Notes</label>
          <input
            value={feederForm.notes}
            onChange={(e) => updFeeder('notes', e.target.value)}
            placeholder="Any notes about this batch..."
          />
        </div>
      </div>
    );
  }

  // R25: an existing batch is edited in place on ITS record. Once that record
  // is no longer the one shown (hub, another batch) and nothing is unsaved or
  // closing, the editor closes rather than staying open off-screen. (The
  // modal used to stay open over the hub.)
  React.useEffect(() => {
    if (!showFeederForm || !editFeederId) return;
    if (recordMode && recordGroup && recordGroup.id === editFeederId) return;
    if (feederClosing.current || feederAutosave.isDirty() || partitionDirtyRef.current) return;
    setShowFeederForm(false);
    setEditFeederId(null);
    setOriginalFeederForm(null);
  }, [
    showFeederForm,
    editFeederId,
    recordMode,
    recordGroup,
    feederAutosave,
    setShowFeederForm,
    setEditFeederId,
    setOriginalFeederForm,
  ]);

  const pigBatchPageView = {
    // R25: the batch's own editor, owned here and rendered in place on the
    // record's Batch Details card. Same handlers as the modal had: the field
    // block, closeFeederForm (Done: draft flush, then partition flush) and
    // deleteFeeder, all held by feederClosing.
    feederEditor: {
      editingId: showFeederForm && editFeederId ? editFeederId : null,
      renderFields: renderFeederFields,
      conflictNotice: <PigConflictNotice conflict={feederConflict} />,
      close: closeFeederForm,
      remove: deleteFeeder,
      busy: feederBusy,
    },
    confirmDelete,
    pigAutoSaveTimer,
    subAutoSaveTimer,
    tripAutoSaveTimer,
    isManager,
    notice,
    setNotice,
    effectiveAdgLbsPerDay,
    systemAdgEstimate,
    dailysForName,
    archiveBatch,
    unarchiveBatch,
    // The five pig.batches.core values the record page used to take off usePig()
    // itself. They travel on the bundle rather than being re-read there because
    // the record page is rendered INLINE by this component off the same
    // resource: a second useRouteResource call would be a second owner of one
    // render, and re-reading them from the provider would leave the
    // `pigbatches` retirement in tests/fixtures/former-eager-dependencies.json a
    // false claim while looking green.
    breedingCycles,
    breeders,
    farrowingRecs,
    feederGroups,
    boarNames,
    processingTripOrdinals: batchesData?.processingTripOrdinals ?? {},
    ...mortality,
    ...subBatches,
    ...plannedTrips,
    ...processingTrips,
  };
  return (
    <div data-pig-feeders-loaded={feedersLoaded ? 'true' : 'false'}>
      {!recordMode && <Header />}
      {!recordMode && (
        <header style={{maxWidth: 1180, margin: '0 auto', padding: '20px 16px 0'}}>
          <h1 style={{fontSize: 30, fontWeight: 800, margin: 0}}>Pig Batches</h1>
          <p style={{color: 'var(--text-2)'}}>Batch counts, feed and processing plans</p>
        </header>
      )}
      <div style={{padding: '0 12px'}}>
        <InlineNotice notice={notice as Notice | null} onDismiss={() => setNotice(null)} />
        {allocationFailed && (
          <Button onClick={() => setAllocationRetry((value) => value + 1)}>Retry Planned Trips</Button>
        )}
        {processingTrips.tripRefreshPending && (
          <Button onClick={() => void processingTrips.retryTripRefresh()}>Retry Refresh</Button>
        )}
      </div>
      {/* Global ADG (commit 4a). Manual override + live system estimate.
        Manager-and-above (admin role for v1) can edit; operators read-only.
        Persists to app_store ppp-pig-global-adg-v1. No reset button per
        Codex; admin types a new value to change. Hub-only — farm-wide, not per
        batch, so it does not render on a single-batch record page. */}
      {!recordMode && (
        <div
          style={{
            padding: '10px 14px',
            margin: '8px 12px',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
            <span style={{fontSize: 11, color: 'var(--ink-muted)', textTransform: 'uppercase', fontWeight: 600}}>
              Global ADG
            </span>
            {!adgEditing && (
              <span style={{fontSize: 14, fontWeight: 700, color: 'var(--ink)'}}>
                {effectiveAdgLbsPerDay != null
                  ? `${(Math.round(effectiveAdgLbsPerDay * 100) / 100).toFixed(2)} lb/day`
                  : '— Projection unavailable'}
              </span>
            )}
            {!adgEditing && globalAdgRow && globalAdgRow.manualValue != null && <Badge variant="neutral">Manual</Badge>}
            {!adgEditing && isManager && (
              <Button
                onClick={() => {
                  setAdgInput(
                    effectiveAdgLbsPerDay != null ? String(Math.round(effectiveAdgLbsPerDay * 100) / 100) : '',
                  );
                  setAdgEditing(true);
                }}
              >
                Edit
              </Button>
            )}
            {adgEditing && (
              <>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={adgInput}
                  onChange={(e) => setAdgInput(e.target.value)}
                  placeholder="lb/day"
                  style={{
                    fontSize: 13,
                    padding: '4px 8px',
                    border: '1px solid var(--border-strong)',
                    borderRadius: 10,
                    width: 110,
                    fontFamily: 'inherit',
                  }}
                />
                <Button
                  onClick={() => {
                    setNotice(null);
                    const v = parseFloat(adgInput);
                    if (!isFinite(v) || v <= 0) {
                      setNotice({kind: 'error', message: 'Enter a positive number for Global ADG.'});
                      return;
                    }
                    persistGlobalAdg(v);
                  }}
                  disabled={adgSaving}
                >
                  {adgSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button onClick={() => setAdgEditing(false)}>Cancel</Button>
              </>
            )}
          </div>
          <div style={{fontSize: 10, color: 'var(--ink-muted)'}}>
            {systemAdgEstimate
              ? `System estimate: ${(Math.round(systemAdgEstimate.valueLbsPerDay * 100) / 100).toFixed(2)} lb/day from ${systemAdgEstimate.sampleCount} session${systemAdgEstimate.sampleCount === 1 ? '' : 's'}`
              : 'System estimate: — (need pig sessions with weights and known age)'}
            {globalAdgRow && globalAdgRow.manualValue != null && globalAdgRow.updatedAt
              ? ` · manual set ${(globalAdgRow.updatedAt || '').slice(0, 10)}`
              : ''}
          </div>
        </div>
      )}
      <div style={{padding: '1rem'}}>
        {!recordMode && (
          <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 12}}>
            <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
              <Button
                variant="primary"
                onClick={() => {
                  setNotice(null);
                  setFeederForm({
                    batchName: '',
                    cycleId: '',
                    giltCount: 0,
                    boarCount: 0,
                    startDate: '',
                    originalPigCount: 0,
                    perLbFeedCost: 0,
                    legacyFeedLbs: 0,
                    notes: '',
                    status: 'active',
                  });
                  setEditFeederId(null);
                  setShowFeederForm(true);
                  setActiveTripBatchId(null);
                  setShowSubForm(null);
                }}
              >
                + Add Manual Batch
              </Button>
            </div>
          </div>
        )}

        {/* Add batch form. R25: an EXISTING batch is edited in place on its
            record (the Batch Details card, which renders the same field block
            through renderFeederFields); this modal is creation only. */}
        {showFeederForm && !editFeederId && (
          <Modal
            title="Add Pig Batch"
            onClose={closeFeederForm}
            footer={
              <ModalFooter
                primaryLabel="Add Batch"
                busy={feederBusy}
                cancelDisabled={feederBusy}
                onPrimary={() => void createFeeder()}
              />
            }
          >
            <div style={{padding: '16px 20px'}}>
              <InlineNotice notice={notice as Notice | null} onDismiss={() => setNotice(null)} />
              <PigConflictNotice conflict={feederConflict} />
              {renderFeederFields()}
            </div>
          </Modal>
        )}

        {/* Three hub states where there used to be one. The batch array arrived
            with the boot payload, so "empty" and "not loaded yet" were the same
            frame and "unreachable" could not happen at all. The empty message
            answers only the first — "No pig batches yet" over a farm with a
            full roster is the empty-farm lie, and here it actively invites a
            duplicate manual batch. */}
        {!recordMode && !feedersLoaded && (
          <div
            data-pig-batches-loading
            style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
          >
            Loading pig batches…
          </div>
        )}

        {!recordMode && feedersLoaded && Boolean(batchesResource.error) && (
          <div
            data-pig-batches-load-error
            style={{textAlign: 'center', padding: '3rem', color: 'var(--danger-ink)', fontSize: 13}}
          >
            Could not load pig batches.{' '}
            <Button type="button" onClick={() => batchesResource.refetch()}>
              Retry
            </Button>
          </div>
        )}

        {!recordMode &&
          feedersLoaded &&
          !batchesResource.error &&
          (feederGroups || []).length === 0 &&
          !showFeederForm && (
            <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}>
              No pig batches yet — farm-born batches appear here once you record the cycle's first farrowing; use "+ Add
              Manual Batch" for manual/admin batches.
            </div>
          )}

        {/* Filtered-no-results: there ARE batches in scope but the active
            filters match none. Distinct from the true-empty message above so
            the operator knows to widen/clear filters rather than add a batch. */}

        {/* Record-page loading: the batch array hasn't resolved yet. Keyed on
            the real readiness signal (feedersLoaded) — NOT feederGroups.length,
            which can't tell "still loading" from "loaded and genuinely empty"
            and races record-page tests on cold start. Showing the not-found
            state during this window would flash "Batch not found" on a valid
            deep-link before data loads. The signal is pig.batches.core's own
            settle now rather than App's boot flag; the property it has to have
            is unchanged. */}
        {recordMode && !recordGroup && !feedersLoaded && (
          <RecordPageLoading Header={Header} label="Loading pig batch…" />
        )}

        {/* Record-page load failure — a THIRD state the boot flag could not
            express. `find(...)` resolves to undefined on a failed read exactly
            as it does on a bad id, so without this branch an app_store outage
            would render "Batch not found." on a batch that exists, which is the
            empty-farm lie in the form that costs the most: it is
            indistinguishable from a deletion. Rendered as a plain div rather
            than a third RecordPage* state, because surface_router_derivation
            reads those to classify a component and this one is a hub with an
            inline record mode. */}
        {recordMode && !recordGroup && feedersLoaded && Boolean(batchesResource.error) && (
          <div
            data-pig-batch-record-load-error
            style={{textAlign: 'center', padding: '3rem', color: 'var(--danger-ink)', fontSize: 13}}
          >
            Could not load this pig batch.{' '}
            <Button type="button" onClick={() => batchesResource.refetch()}>
              Retry
            </Button>
          </div>
        )}

        {/* Record-page not-found: data loaded successfully, but the URL id is
            genuinely absent from the feeder groups. */}
        {recordMode && !recordGroup && feedersLoaded && !batchesResource.error && (
          <RecordPageNotFound
            Header={Header}
            backLabel="Back to Pig Batches"
            onBack={goToHub}
            message="Batch not found."
          />
        )}

        {/* Hub: one unified vertical inspection grid — one row per visible
            batch, active and processed in the same row stack (the Show/Hide
            processed toggle controls whether processed rows are included).
            Consistent columns let every batch's key metrics be compared at a
            glance; clicking a row opens that batch's record-page workspace.
            The header row and each batch row share PIG_BATCH_GRID_COLUMNS so
            the columns stay aligned. Horizontal scroll on narrow screens keeps
            the table readable without collapsing the column alignment.
            R19 (a): no sticky column; below the grid's width each row is a
            card with every cell labelled (pigBatchesGrid.css). Desktop fit
            via Columns (R19 b) waits for the shared R17 list helper. */}
        {!recordMode && visiblePigBatches.length > 0 && (
          <div
            className="pig-batches-grid-wrap"
            // R08: one card, radius 18 on --shadow-card.
            style={{
              overflowX: 'auto',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              background: 'var(--surface)',
            }}
          >
            <div data-pig-batch-grid="1" style={{minWidth: 1390}}>
              <div
                className="pig-batches-grid-head"
                style={{
                  display: 'grid',
                  gridTemplateColumns: PIG_BATCH_GRID_COLUMNS,
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 14px',
                  // R02 / R29: heads written once, 11.5px uppercase in --label,
                  // over a hairline.
                  borderBottom: '1px solid var(--hairline)',
                  background: 'var(--surface)',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                {PIG_BATCH_GRID_HEADERS.map((h, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                      color: 'var(--label)',
                      textAlign: i >= 2 ? 'right' : 'left',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>
              {visiblePigBatches
                .filter((g) => g.status !== 'processed')
                .map((g) => renderPigBatchTile(g, sequencePigBatches))}
              {visiblePigBatches.some((g) => g.status === 'processed') && (
                <Collapsible
                  label="Complete"
                  count={visiblePigBatches.filter((g) => g.status === 'processed').length}
                  open={showArchBatches}
                  onOpenChange={setShowArchBatches}
                  defaultOpen={false}
                >
                  {visiblePigBatches
                    .filter((g) => g.status === 'processed')
                    .map((g) => renderPigBatchTile(g, sequencePigBatches))}
                </Collapsible>
              )}
            </div>
          </div>
        )}

        {/* Data-provenance note — only when a visible row carries proportional
            sex-split estimates (source records without sex attribution). */}
        {!recordMode && visiblePigBatches.some((g) => (pigBatchMetricsById[g.id] || {}).hasEstimates) && (
          <div data-pig-batch-estimate-note="1" style={{fontSize: 11, color: 'var(--ink-muted)', margin: '6px 2px 0'}}>
            Sex-split current/feed values marked ~ are estimated from started-head split when records lack sex
            attribution.
          </div>
        )}

        {/* Record page: the selected batch's full workspace (one group). The
            card body below is unchanged from the legacy inline render; only its
            source is narrowed to the routed group (processed batches still open
            via direct link). */}
        {recordMode && recordGroup && (
          <PigBatchPage
            Header={Header}
            group={recordGroup}
            view={pigBatchPageView}
            recordSeq={recordSeq}
            recordId={recordId}
            onNavigateSeq={navigateSeq}
            onBack={goToHub}
          />
        )}
      </div>
    </div>
  );
}
